/**
 * Contract detail — read-only. Surfaces the immutable snapshots the
 * contract was signed against, plus the PDF reference and the hash,
 * with cross-links to the loan + customer + device the contract
 * binds.
 *
 * Snapshot rendering: known shapes (loan terms, device terms, lock
 * policy, payment schedule) get dedicated structured panels with
 * money + dates formatted for human reading. Unknown / future shapes
 * fall back to a key/value list (NOT raw JSON) — a "Show raw" toggle
 * exposes the underlying JSON for ops triage.
 */
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useState } from "react";
import type { Contract, Customer, Device, Loan } from "@dfp/api-client";
import { Badge } from "../components/Badge.js";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { Table, TBody, Td, Th, THead, Tr } from "../components/Table.js";
import { api } from "../lib/api.js";
import { formatMoney } from "../lib/utils.js";

export function ContractDetailPage(): JSX.Element {
  const { id } = useParams({ strict: false }) as { id: string };
  const contract = useQuery({
    queryKey: ["contracts", "byId", id],
    queryFn: () => api.get<Contract>(`/api/contracts/${id}`),
  });

  // Linked aggregates — fetched lazily once the contract's loanId is
  // available so a stale / broken loan link doesn't block the page.
  const loanId = contract.data?.loanId;
  const loanQuery = useQuery({
    queryKey: ["loans", "byId", loanId],
    queryFn: () => api.get<Loan>(`/api/loans/${loanId}`),
    enabled: Boolean(loanId),
  });
  const loan = loanQuery.data;
  const customerQuery = useQuery({
    queryKey: ["customers", "byId", loan?.customerId],
    queryFn: () => api.get<Customer>(`/api/customers/${loan?.customerId}`),
    enabled: Boolean(loan?.customerId),
  });
  const deviceQuery = useQuery({
    queryKey: ["devices", "byId", loan?.deviceId],
    queryFn: () => api.get<Device>(`/api/devices/${loan?.deviceId}`),
    enabled: Boolean(loan?.deviceId),
  });

  if (contract.isLoading) return <p className="text-sm text-slate-500">Loading…</p>;
  if (contract.isError || !contract.data) {
    return <p className="text-sm text-red-700">Failed to load contract.</p>;
  }
  const c = contract.data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">
          Contract · {c.templateId} v{c.version}
        </h1>
        <p className="text-xs font-mono text-slate-500">{c.id}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Related</CardTitle>
        </CardHeader>
        <CardBody className="grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
          <CrossLink
            label="Loan"
            to="/loans/$id"
            id={c.loanId}
            subtitle={loan ? `Status: ${loan.status}` : "—"}
          />
          <CrossLink
            label="Customer"
            to="/customers/$id"
            id={customerQuery.data?.id}
            subtitle={
              customerQuery.data
                ? `${customerQuery.data.givenName} ${customerQuery.data.familyName}`
                : "—"
            }
          />
          <CrossLink
            label="Device"
            to="/devices/$id"
            id={deviceQuery.data?.id}
            subtitle={
              deviceQuery.data
                ? `${deviceQuery.data.brand} ${deviceQuery.data.model} · IMEI ${deviceQuery.data.imei}`
                : "—"
            }
          />
        </CardBody>
      </Card>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Signature</CardTitle>
          </CardHeader>
          <CardBody className="space-y-2 text-sm">
            <Row k="Signed at" v={new Date(c.signedAt).toLocaleString()} />
            <Row k="Language" v={c.language} />
            <Row k="Customer signature ref" v={shorten(c.signedByCustomer)} />
            <Row k="Dealer signature ref" v={shorten(c.signedByDealer)} />
            <Row
              k="Dealer GPS"
              v={`${c.gpsLat.toFixed(5)}, ${c.gpsLng.toFixed(5)} (±${c.gpsAccuracyMetres}m)`}
            />
            <Row k="Dealer device fingerprint" v={shorten(c.dealerDeviceFingerprint)} />
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Integrity</CardTitle>
          </CardHeader>
          <CardBody className="space-y-2 text-sm">
            <Row k="PDF ref" v={shorten(c.pdfRef)} />
            <Row k="JSON hash" v={c.jsonHash} />
            <Row k="Hash signature" v={shorten(c.jsonHashSignature)} />
            <p className="pt-2 text-xs text-slate-500">
              The JSON snapshot is canonicalised and SHA-256 hashed; the hash is signed with the
              contract-signing key from <code>@dfp/crypto</code>. Modifying any snapshot field
              breaks the hash.
            </p>
          </CardBody>
        </Card>
      </div>

      <LoanTermsCard data={c.loanTermsSnapshot} />
      <DeviceTermsCard data={c.deviceTermsSnapshot} />
      <LockPolicyCard data={c.lockPolicySnapshot} />
      <PaymentScheduleCard data={c.paymentScheduleSnapshot} />
    </div>
  );
}

function CrossLink({
  label,
  to,
  id,
  subtitle,
}: {
  label: string;
  to: "/loans/$id" | "/customers/$id" | "/devices/$id";
  id: string | undefined;
  subtitle: string;
}): JSX.Element {
  return (
    <div className="rounded-md border border-slate-200 p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
        {id ? (
          <Link
            to={to}
            params={{ id }}
            className="font-mono text-xs text-brand-600 hover:underline"
          >
            {id.slice(0, 8)}…
          </Link>
        ) : (
          <Badge tone="neutral">Loading…</Badge>
        )}
      </div>
      <p className="mt-1 text-sm text-slate-700">{subtitle}</p>
    </div>
  );
}

/**
 * Loan terms snapshot — { currency, frequency, tenorMonths,
 * principalMinor, depositMinor, policyVersionId }. Money fields are
 * formatted in the snapshot currency; unknown extra keys flow through
 * a generic "Other terms" row so newer contracts don't lose data.
 */
function LoanTermsCard({ data }: { data: Record<string, unknown> }): JSX.Element {
  const currency = readString(data, "currency") ?? "TZS";
  const frequency = readString(data, "frequency");
  const tenor = readNumber(data, "tenorMonths");
  const principal = readNumber(data, "principalMinor");
  const deposit = readNumber(data, "depositMinor");
  const policy = readString(data, "policyVersionId");
  const known = new Set([
    "currency",
    "frequency",
    "tenorMonths",
    "principalMinor",
    "depositMinor",
    "policyVersionId",
  ]);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Loan terms</CardTitle>
        {frequency || tenor ? (
          <Badge tone="neutral">
            {frequency ?? "?"}
            {tenor != null ? ` · ${tenor}mo` : ""}
          </Badge>
        ) : null}
      </CardHeader>
      <CardBody className="space-y-2 text-sm">
        <Row k="Principal" v={principal != null ? formatMoney(principal, currency) : "—"} />
        <Row k="Deposit" v={deposit != null ? formatMoney(deposit, currency) : "—"} />
        <Row k="Currency" v={currency} />
        <Row k="Repayment frequency" v={frequency ?? "—"} />
        <Row k="Tenor (months)" v={tenor != null ? String(tenor) : "—"} />
        <Row k="Policy version" v={policy ?? "—"} />
        <ExtraFields data={data} known={known} />
      </CardBody>
    </Card>
  );
}

/**
 * Device terms snapshot — { imei, brand, model, serial, costMinor,
 * lendingMode, lendingPriceMinor, downPaymentMinor }. We split the
 * card into two sub-sections: the device facts and the financing
 * terms, because the operator usually scans for one or the other.
 */
function DeviceTermsCard({ data }: { data: Record<string, unknown> }): JSX.Element {
  // Currency isn't carried on this snapshot today; assume TZS — fall back
  // to the parent contract's loan-terms.currency when that lands.
  const currency = "TZS";
  const brand = readString(data, "brand");
  const model = readString(data, "model");
  const imei = readString(data, "imei");
  const serial = readString(data, "serial");
  const cost = readNumber(data, "costMinor");
  const lendingMode = readString(data, "lendingMode");
  const lendingPrice = readNumber(data, "lendingPriceMinor");
  const downPayment = readNumber(data, "downPaymentMinor");
  const known = new Set([
    "imei",
    "brand",
    "model",
    "serial",
    "costMinor",
    "lendingMode",
    "lendingPriceMinor",
    "downPaymentMinor",
  ]);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Device terms</CardTitle>
        {brand || model ? (
          <Badge tone="neutral">
            {brand ?? ""} {model ?? ""}
          </Badge>
        ) : null}
      </CardHeader>
      <CardBody className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <section className="space-y-2 text-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Device</p>
          <Row k="Brand" v={brand ?? "—"} />
          <Row k="Model" v={model ?? "—"} />
          <Row k="IMEI" v={imei ?? "—"} />
          <Row k="Serial" v={serial ?? "—"} />
        </section>
        <section className="space-y-2 text-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Financing</p>
          <Row k="Cost" v={cost != null ? formatMoney(cost, currency) : "—"} />
          <Row
            k="Lending price"
            v={lendingPrice != null ? formatMoney(lendingPrice, currency) : "—"}
          />
          <Row
            k="Down payment"
            v={downPayment != null ? formatMoney(downPayment, currency) : "—"}
          />
          <Row k="Lending mode" v={lendingMode ?? "—"} />
        </section>
        <div className="md:col-span-2">
          <ExtraFields data={data} known={known} />
        </div>
      </CardBody>
    </Card>
  );
}

/** Lock policy snapshot — { riskClass, controlProvider }. */
function LockPolicyCard({ data }: { data: Record<string, unknown> }): JSX.Element {
  const riskClass = readString(data, "riskClass");
  const controlProvider = readString(data, "controlProvider");
  const known = new Set(["riskClass", "controlProvider"]);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Lock policy</CardTitle>
        {riskClass ? <Badge tone="neutral">Risk class {riskClass}</Badge> : null}
      </CardHeader>
      <CardBody className="space-y-2 text-sm">
        <Row k="Risk class" v={riskClass ?? "—"} />
        <Row k="Control provider" v={controlProvider ?? "—"} />
        <ExtraFields data={data} known={known} />
      </CardBody>
    </Card>
  );
}

/**
 * Payment-schedule snapshot — { installments: [{ installmentNumber,
 * dueAt, principalMinor, interestMinor, feesMinor }, ...] }. Renders
 * the first 12 rows by default + an "Show all N" toggle; otherwise
 * the daily-frequency contracts (180 rows) flood the page.
 *
 * Footer row computes principal/interest/fees/total sums so the
 * operator can sanity-check the totals against the loan's money card
 * without scrolling through every installment.
 */
function PaymentScheduleCard({ data }: { data: Record<string, unknown> }): JSX.Element {
  const [showAll, setShowAll] = useState(false);
  const installmentsRaw = (data as { installments?: unknown }).installments;
  const installments = Array.isArray(installmentsRaw)
    ? (installmentsRaw as ReadonlyArray<Record<string, unknown>>)
    : [];
  const currency = "TZS"; // see DeviceTermsCard note
  const totalPrincipal = installments.reduce(
    (s, r) => s + (readNumber(r, "principalMinor") ?? 0),
    0,
  );
  const totalInterest = installments.reduce((s, r) => s + (readNumber(r, "interestMinor") ?? 0), 0);
  const totalFees = installments.reduce((s, r) => s + (readNumber(r, "feesMinor") ?? 0), 0);
  const grandTotal = totalPrincipal + totalInterest + totalFees;
  const visible = showAll ? installments : installments.slice(0, 12);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Payment schedule</CardTitle>
        <span className="text-xs text-slate-500">{installments.length} installments</span>
      </CardHeader>
      <CardBody className="p-0">
        {installments.length === 0 ? (
          <p className="p-5 text-sm text-slate-500">No installments in this snapshot.</p>
        ) : (
          <>
            <Table>
              <THead>
                <tr>
                  <Th>#</Th>
                  <Th>Due</Th>
                  <Th>Principal</Th>
                  <Th>Interest</Th>
                  <Th>Fees</Th>
                  <Th>Total</Th>
                </tr>
              </THead>
              <TBody>
                {visible.map((row, idx) => {
                  const num = readNumber(row, "installmentNumber") ?? idx + 1;
                  const dueAt = readString(row, "dueAt");
                  const principal = readNumber(row, "principalMinor") ?? 0;
                  const interest = readNumber(row, "interestMinor") ?? 0;
                  const fees = readNumber(row, "feesMinor") ?? 0;
                  return (
                    <Tr key={num}>
                      <Td className="text-xs text-slate-500">{num}</Td>
                      <Td className="text-xs text-slate-600">
                        {dueAt ? new Date(dueAt).toLocaleDateString() : "—"}
                      </Td>
                      <Td>{formatMoney(principal, currency)}</Td>
                      <Td>{formatMoney(interest, currency)}</Td>
                      <Td>{formatMoney(fees, currency)}</Td>
                      <Td className="font-medium">
                        {formatMoney(principal + interest + fees, currency)}
                      </Td>
                    </Tr>
                  );
                })}
                <Tr>
                  <Td className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Total
                  </Td>
                  <Td>{""}</Td>
                  <Td className="font-semibold">{formatMoney(totalPrincipal, currency)}</Td>
                  <Td className="font-semibold">{formatMoney(totalInterest, currency)}</Td>
                  <Td className="font-semibold">{formatMoney(totalFees, currency)}</Td>
                  <Td className="font-semibold text-slate-900">
                    {formatMoney(grandTotal, currency)}
                  </Td>
                </Tr>
              </TBody>
            </Table>
            {installments.length > 12 ? (
              <div className="border-t border-slate-200 px-5 py-2 text-right">
                <button
                  type="button"
                  className="text-xs text-brand-600 hover:underline"
                  onClick={() => setShowAll((v) => !v)}
                >
                  {showAll ? "Collapse" : `Show all ${installments.length}`}
                </button>
              </div>
            ) : null}
          </>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * Render any keys NOT in the `known` set as a generic key/value list.
 * Lets newer snapshot shapes ship extra fields without losing them in
 * the UI. Falls back to a Show-raw-JSON disclosure for nested objects.
 */
function ExtraFields({
  data,
  known,
}: {
  data: Record<string, unknown>;
  known: ReadonlySet<string>;
}): JSX.Element | null {
  const extras = Object.entries(data).filter(([k]) => !known.has(k));
  if (extras.length === 0) return null;
  return (
    <details className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs">
      <summary className="cursor-pointer font-medium text-slate-700">
        Other fields ({extras.length})
      </summary>
      <dl className="mt-2 space-y-1">
        {extras.map(([k, v]) => (
          <div key={k} className="flex items-baseline justify-between gap-3">
            <dt className="text-slate-500">{k}</dt>
            <dd className="font-mono text-slate-800">
              {typeof v === "object" ? JSON.stringify(v) : String(v)}
            </dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

function readString(o: Record<string, unknown>, k: string): string | undefined {
  const v = o[k];
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

function readNumber(o: Record<string, unknown>, k: string): number | undefined {
  const v = o[k];
  return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}

function Row({ k, v }: { k: string; v: string }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-xs uppercase tracking-wide text-slate-500">{k}</span>
      <span className="text-sm font-medium text-slate-900 truncate font-mono">{v}</span>
    </div>
  );
}

function shorten(s: string): string {
  return s.length > 28 ? `${s.slice(0, 14)}…${s.slice(-10)}` : s;
}
